/**
 * Système de vérification des permissions côté frontend
 * Synchronisé avec le système backend
 */

/**
 * Mapping des routes vers les permissions requises
 * Format: "route_path": ["permission1", "permission2", ...]
 *
 * Note: Si plusieurs permissions sont listées, l'utilisateur doit en avoir AU MOINS UNE
 */
export const ROUTE_PERMISSIONS = {
  // Dashboard
  "/dashboard": ["dashboard:view"],
  "/executive-dashboard": ["reports:view_financial", "dashboard:view_stats"],

  // Configuration Société
  "/company-configuration": ["company:view", "company:update"],
  "/organigramme": ["company:view_orgchart", "company:update_orgchart"],
  "/positions": ["company:view", "company:manage_jobs"],

  // Gestion RH
  "/employees": [
    "employees:view",
    "employees:view_team",
    "employees:view_department",
    "employees:view_site",
    "employees:view_own",
  ],
  "/planning": [
    "planning:view",
    "planning:view_team",
    "planning:view_department",
    "planning:view_site",
    "planning:view_own",
  ],
  "/gestion-absences": ["absences:manage_reasons", "absences:manage_workflows"],
  "/document-templates": ["documents:manage_templates"],

  // Jours Fériés
  "/jours-feries": ["holidays:view", "holidays:view_own", "holidays:manage_calendars", "holidays:assign_calendars"],
  "/leave-policies": ["absences:manage_reasons"],

  // Paie & Rémunération (compensation/benefits/expenses share the payroll module)
  "/compensation": ["payroll:view", "payroll:calculate", "payroll:approve"],
  "/benefits": ["payroll:view", "payroll:calculate"],
  "/expenses": ["payroll:view", "payroll:approve"],

  // Recrutement & Cycle de vie
  "/recrutement": ["employees:view", "employees:update"],
  "/interview-scheduling": ["employees:view", "employees:update"],
  "/esignature": ["documents:create", "security:manage"],
  "/onboarding": ["employees:view", "employees:update"],
  "/offboarding": ["employees:view", "employees:update"],

  // Talent & Développement
  "/performance": ["employees:view", "employees:update", "employees:view_own"],
  "/one-on-one": ["employees:view", "employees:view_team", "employees:update"],
  "/recognition": ["employees:update"],
  "/skills": ["employees:view", "employees:view_team", "employees:update", "company:manage_jobs"],
  "/succession": ["employees:view", "employees:update"],
  "/career": ["employees:view", "employees:update"],
  "/lms": ["employees:view", "employees:view_team", "employees:update"],
  "/engagement": ["employees:view", "employees:view_team", "employees:update"],
  "/wellbeing": ["employees:view", "employees:view_team", "employees:update"],

  // Pilotage & Analytics
  "/workforce-planning": ["employees:view", "employees:update"],
  "/analytics": ["dashboard:view_stats", "reports:view_advanced"],
  "/report-builder": ["reports:view", "reports:create"],

  // IA & Automatisation
  "/hr-analyst": ["employees:view", "payroll:view"],
  "/ai-contract-generator": ["documents:view", "documents:create"],
  "/workflow-automation": ["security:manage"],

  // Conformité & Sécurité
  "/audit-trail": ["security:view", "security:manage"],
  "/compliance": ["payroll:export"],
  "/gdpr": ["security:view", "security:manage"],

  // Intégrations
  "/sso": ["security:view", "security:manage"],
  "/integrations": ["security:view", "security:manage"],

  // Mon Espace (employee self-service)
  "/mon-espace": [
    "employees:view_own",
    "payroll:view_own",
    "absences:view_own",
    "planning:view_own",
    "holidays:view_own",
  ],

  // Paie
  "/paie": [
    "payroll:view",
    "payroll:view_own",
    "payroll:view_team",
    "payroll:calculate",
    "payroll:approve",
    "payroll:manage_periods",
    "payroll:export",
  ],

  // Infrastructure
  "/terminals": ["terminals:view", "terminals:configure"],
  "/zkteco-monitoring": ["terminals:monitor"],
  "/biometric-mapping": ["terminals:manage_biometric"],

  // Pointages
  "/pointages": [
    "attendances:view",
    "attendances:view_team",
    "attendances:view_department",
    "attendances:view_site",
    "attendances:view_own",
  ],
  "/pointages-advanced": [
    "attendances:view",
    "attendances:view_team",
    "attendances:validate",
    "attendances:validate_team",
  ],

  // Rapports
  "/reports": ["reports:view", "reports:view_team"],
  "/advanced-reports": ["reports:view_advanced", "reports:view_financial"],

  // Administration
  "/settings": ["administration:manage_settings"],
  "/security-roles": ["administration:manage_roles", "administration:manage_permissions", "administration:view_users"],
  "/security-center": ["security:view", "security:manage"],
};

const SECURITY_CENTER_ROLES = [
  "ADMIN",
  "ADMIN_IT",
  "Administrateur",
  "admin",
  "super_admin",
  "RH",
  "rh",
  "HR",
  "hr",
  "HR_MANAGER",
  "HR Manager",
  "Responsable RH",
];

/**
 * Vérifie si l'utilisateur a une permission spécifique
 * @param {Object} user - Objet utilisateur avec roles et permissions
 * @param {string} permission - Permission à vérifier (format: "module:action")
 * @returns {boolean}
 */
export function hasPermission(user, permission) {
  if (!user || !user.permissions) {
    return false;
  }

  // Wildcard admin: accès à tout
  if (user.permissions.includes("*:*")) {
    return true;
  }

  // Permission exacte
  if (user.permissions.includes(permission)) {
    return true;
  }

  // Permission wildcard par module (ex: employees:*)
  const [module] = permission.split(":");
  if (user.permissions.includes(`${module}:*`)) {
    return true;
  }

  return false;
}

/**
 * Vérifie si l'utilisateur a AU MOINS UNE des permissions listées
 * @param {Object} user - Objet utilisateur
 * @param {string[]} permissions - Liste de permissions
 * @returns {boolean}
 */
export function hasAnyPermission(user, permissions) {
  if (!permissions || permissions.length === 0) {
    return true; // Pas de restriction
  }

  return permissions.some((perm) => hasPermission(user, perm));
}

/**
 * Vérifie si l'utilisateur a TOUTES les permissions listées
 * @param {Object} user - Objet utilisateur
 * @param {string[]} permissions - Liste de permissions
 * @returns {boolean}
 */
export function hasAllPermissions(user, permissions) {
  if (!permissions || permissions.length === 0) {
    return true;
  }

  return permissions.every((perm) => hasPermission(user, perm));
}

/**
 * Vérifie si l'utilisateur a un rôle spécifique
 * @param {Object} user - Objet utilisateur
 * @param {string} roleName - Nom du rôle
 * @returns {boolean}
 */
export function hasRole(user, roleName) {
  if (!user || !user.roles) {
    return false;
  }

  return user.roles.includes(roleName);
}

export function hasAnyRole(user, roleNames) {
  if (!user || !user.roles) {
    return false;
  }

  return roleNames.some((roleName) => user.roles.includes(roleName));
}

/**
 * Vérifie si l'utilisateur peut accéder à une route
 * @param {Object} user - Objet utilisateur
 * @param {string} path - Chemin de la route
 * @returns {boolean}
 */
export function canAccessRoute(user, path) {
  if (path === "/security-center" && hasAnyRole(user, SECURITY_CENTER_ROLES)) {
    return true;
  }

  // Si aucune permission n'est définie pour cette route, on autorise l'accès
  const requiredPermissions = ROUTE_PERMISSIONS[path];

  if (!requiredPermissions) {
    return true;
  }

  // L'utilisateur doit avoir AU MOINS UNE des permissions requises
  return hasAnyPermission(user, requiredPermissions);
}

/**
 * Filtre un élément de menu selon les permissions de l'utilisateur
 * @param {Object} user - Objet utilisateur
 * @param {Object} menuItem - Élément de menu
 * @returns {Object|null} - Élément de menu filtré ou null si non autorisé
 */
export function filterMenuItem(user, menuItem) {
  // Si c'est un élément avec des sous-items
  if (menuItem.items && menuItem.items.length > 0) {
    const filteredItems = menuItem.items.map((item) => filterMenuItem(user, item)).filter((item) => item !== null);

    // Si tous les sous-items sont filtrés, masquer le menu parent
    if (filteredItems.length === 0) {
      return null;
    }

    return {
      ...menuItem,
      items: filteredItems,
    };
  }

  // Si c'est un élément simple avec un path
  if (menuItem.path) {
    // Vérifier si l'utilisateur peut accéder à cette route
    if (!canAccessRoute(user, menuItem.path)) {
      return null;
    }
  }

  return menuItem;
}

/**
 * Filtre tous les éléments de menu selon les permissions
 * @param {Object} user - Objet utilisateur
 * @param {Array} menuItems - Liste des éléments de menu
 * @returns {Array} - Liste des éléments de menu filtrés
 */
export function filterMenuItems(user, menuItems) {
  if (!user) {
    return [];
  }

  return menuItems.map((item) => filterMenuItem(user, item)).filter((item) => item !== null);
}

/**
 * Vérifie si l'utilisateur est admin
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export function isAdmin(user) {
  return hasPermission(user, "*:*") || hasRole(user, "ADMIN");
}

/**
 * Vérifie si l'utilisateur est RH
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export function isRH(user) {
  return isAdmin(user) || hasRole(user, "RH");
}

/**
 * Vérifie si l'utilisateur est chef d'équipe
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export function isTeamLead(user) {
  return hasRole(user, "TEAM_LEAD");
}

/**
 * Vérifie si l'utilisateur est un employé simple
 * @param {Object} user - Objet utilisateur
 * @returns {boolean}
 */
export function isEmploye(user) {
  return hasRole(user, "EMPLOYE") && !isAdmin(user) && !isRH(user) && !isTeamLead(user);
}

/**
 * Retourne le niveau d'accès de l'utilisateur (pour affichage)
 * @param {Object} user - Objet utilisateur
 * @returns {string}
 */
export function getUserAccessLevel(user) {
  if (isAdmin(user)) return "Administrateur";
  if (isRH(user)) return "Ressources Humaines";
  if (isTeamLead(user)) return "Chef d'Équipe";
  if (hasRole(user, "COMPTABLE")) return "Comptable";
  if (isEmploye(user)) return "Employé";
  return "Utilisateur";
}

/**
 * Composant React pour afficher un élément uniquement si l'utilisateur a la permission
 * @param {Object} props - Props du composant
 * @param {Object} props.user - Objet utilisateur
 * @param {string|string[]} props.permission - Permission(s) requise(s)
 * @param {boolean} props.requireAll - Si true, toutes les permissions sont requises. Si false, au moins une
 * @param {React.ReactNode} props.children - Contenu à afficher
 * @param {React.ReactNode} props.fallback - Contenu de remplacement si non autorisé
 */
export function PermissionGuard({ user, permission, requireAll = false, children, fallback = null }) {
  const permissions = Array.isArray(permission) ? permission : [permission];

  const isAuthorized = requireAll ? hasAllPermissions(user, permissions) : hasAnyPermission(user, permissions);

  return isAuthorized ? children : fallback;
}

/**
 * Hook React pour vérifier les permissions
 * @param {Object} user - Objet utilisateur
 * @returns {Object} - Objet avec les fonctions de vérification
 */
export function usePermissions(user) {
  return {
    hasPermission: (permission) => hasPermission(user, permission),
    hasAnyPermission: (permissions) => hasAnyPermission(user, permissions),
    hasAllPermissions: (permissions) => hasAllPermissions(user, permissions),
    hasRole: (roleName) => hasRole(user, roleName),
    canAccessRoute: (path) => canAccessRoute(user, path),
    isAdmin: () => isAdmin(user),
    isRH: () => isRH(user),
    isTeamLead: () => isTeamLead(user),
    isEmploye: () => isEmploye(user),
    getUserAccessLevel: () => getUserAccessLevel(user),
  };
}
